import { lazy, Suspense, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';

import type { AuthMeResponse } from '@/api/auth.api';
import {
  createAnnouncement,
  updateAnnouncement,
  type Announcement,
  type AnnouncementAudience,
} from '@/api/comms/announcements.api';
import { listCommsMembers } from '@/api/comms/members.api';
import { listDepartments } from '@/api/employees.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import type { TipTapDoc } from '@/components/richtext/TipTapRenderer';
import { describeCommsError } from '@/lib/comms/errors';
import { hasCapability, isSuperadmin } from '@/lib/capabilities';

/**
 * Announcement composer (create + edit). Only mounted while the modal is
 * open (the Modal unmounts its children when closed), so the form + the
 * lazy TipTap editor seed cleanly from `editing` on each open.
 *
 * Editing is title / body / requiresAck only — audience is never
 * retargeted after publish (server contract). Department leads are locked
 * to their own live department; capability holders / superadmins may
 * target the org or any department.
 */

const SELECT_CLS =
  'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

const EMPTY_DOC: TipTapDoc = { type: 'doc', content: [{ type: 'paragraph' }] };

const RichTextEditor = lazy(() => import('@/components/richtext/RichTextEditor'));

export function AnnouncementComposerModal({
  open,
  onClose,
  me,
  editing,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  me: AuthMeResponse;
  editing: Announcement | null;
  onSaved: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Edit announcement' : 'New announcement'} size="lg">
      <ComposerForm me={me} editing={editing} onClose={onClose} onSaved={onSaved} />
    </Modal>
  );
}

function ComposerForm({
  me,
  editing,
  onClose,
  onSaved,
}: {
  me: AuthMeResponse;
  editing: Announcement | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEditing = editing !== null;
  const canTargetAny = isSuperadmin(me) || hasCapability(me, 'manage_announcements');
  /** Not a capability holder → a department lead, locked to their own dept. */
  const isDeptLeadOnly = !canTargetAny;

  const [title, setTitle] = useState(editing?.title ?? '');
  const [body, setBody] = useState<TipTapDoc | null>(editing?.bodyJson ?? null);
  const [audience, setAudience] = useState<AnnouncementAudience>(
    editing?.audience ?? (isDeptLeadOnly ? 'department' : 'org'),
  );
  const [selectedDeptId, setSelectedDeptId] = useState<string>(editing?.departmentId ?? '');
  const [requiresAck, setRequiresAck] = useState<boolean>(editing?.requiresAck ?? false);
  const [emailOnPublish, setEmailOnPublish] = useState(false);

  const departmentsQuery = useQuery({ queryKey: ['departments'], queryFn: listDepartments });
  const membersQuery = useQuery({
    queryKey: ['comms', 'members'],
    queryFn: listCommsMembers,
    enabled: isDeptLeadOnly && !isEditing,
  });

  const departments = departmentsQuery.data ?? [];
  const deptName = (id: string | null): string | undefined => departments.find((d) => d.id === id)?.name;

  const myDepartmentId = useMemo(() => {
    const mine = (membersQuery.data ?? []).find((m) => m.membershipId === me.membership.id);
    return mine?.departmentId ?? null;
  }, [membersQuery.data, me.membership.id]);

  const mutation = useMutation({
    mutationFn: () => {
      if (editing) {
        return updateAnnouncement(editing.id, {
          title: title.trim(),
          ...(body ? { bodyJson: body } : {}),
          requiresAck,
        });
      }
      if (isDeptLeadOnly) {
        return createAnnouncement({
          title: title.trim(),
          bodyJson: body ?? EMPTY_DOC,
          audience: 'department',
          departmentId: myDepartmentId,
          requiresAck,
          emailOnPublish,
        });
      }
      return createAnnouncement({
        title: title.trim(),
        bodyJson: body ?? EMPTY_DOC,
        audience,
        departmentId: audience === 'department' ? selectedDeptId : null,
        requiresAck,
        emailOnPublish,
      });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const targetingDept = !isEditing && (isDeptLeadOnly || audience === 'department');
  const missingDept = targetingDept && (isDeptLeadOnly ? myDepartmentId === null : selectedDeptId === '');
  const canSubmit = title.trim().length > 0 && !missingDept;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) mutation.mutate();
      }}
      className="space-y-4"
    >
      <Input
        label="Title"
        name="announcement-title"
        required
        maxLength={200}
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
        }}
        placeholder="e.g. New PTO policy starts Monday"
      />

      <div>
        <span className="mb-1.5 block text-[13px] font-medium text-kw-label">Message</span>
        <Suspense
          fallback={
            <div className="rounded-kw-md border border-kw-border px-3.5 py-6 text-center text-[13px] text-kw-muted">
              Loading editor…
            </div>
          }
        >
          <RichTextEditor value={body} onChange={setBody} />
        </Suspense>
      </div>

      {/* ── Audience ─────────────────────────────────────────────────── */}
      {isEditing ? (
        <p className="rounded-kw-md bg-kw-bg px-3 py-2 text-[12px] text-kw-muted">
          Audience:{' '}
          <span className="font-medium text-kw-shark">
            {editing.audience === 'department'
              ? (deptName(editing.departmentId) ?? 'Department')
              : 'Org-wide'}
          </span>{' '}
          — this can’t change after publishing.
        </p>
      ) : isDeptLeadOnly ? (
        myDepartmentId ? (
          <p className="rounded-kw-md bg-kw-bg px-3 py-2 text-[12px] text-kw-muted">
            Posting to your department:{' '}
            <span className="font-medium text-kw-shark">{deptName(myDepartmentId) ?? 'Department'}</span>
          </p>
        ) : (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-warning-text/30 bg-kw-warning-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-warning-text" />
            <p className="text-[12px] text-kw-warning-text">
              You’re not assigned to a department, so you can’t post a department announcement.
            </p>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Audience</span>
            <select
              aria-label="Audience"
              value={audience}
              onChange={(e) => {
                setAudience(e.target.value as AnnouncementAudience);
              }}
              className={SELECT_CLS}
            >
              <option value="org">Org-wide</option>
              <option value="department">A department</option>
            </select>
          </label>
          {audience === 'department' ? (
            <label className="flex flex-col gap-1">
              <span className="text-[13px] font-medium text-kw-label">Department</span>
              <select
                aria-label="Department"
                value={selectedDeptId}
                onChange={(e) => {
                  setSelectedDeptId(e.target.value);
                }}
                className={SELECT_CLS}
              >
                <option value="">Select a department…</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      )}

      {/* ── Options ──────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-[13px] text-kw-shark">
          <input
            type="checkbox"
            checked={requiresAck}
            onChange={(e) => {
              setRequiresAck(e.target.checked);
            }}
            className="size-4 rounded border-kw-border text-kw-cerulean focus:ring-kw-cerulean/40"
          />
          Require acknowledgment
        </label>
        {!isEditing ? (
          <label className="flex items-center gap-2 text-[13px] text-kw-shark">
            <input
              type="checkbox"
              checked={emailOnPublish}
              onChange={(e) => {
                setEmailOnPublish(e.target.checked);
              }}
              className="size-4 rounded border-kw-border text-kw-cerulean focus:ring-kw-cerulean/40"
            />
            Email everyone in the audience on publish
          </label>
        ) : null}
      </div>

      {mutation.error ? (
        <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
          <p className="text-[12px] text-kw-danger-text">
            {describeCommsError(mutation.error, 'Could not save the announcement.')}
          </p>
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" isLoading={mutation.isPending} disabled={!canSubmit}>
          {isEditing ? 'Save changes' : 'Publish'}
        </Button>
      </div>
    </form>
  );
}
